/**
 * CX Task Tab — "My Listings" workflow (Sections 3 & 4).
 *
 * Moved here from CX Insights. Lists every OTA listing assigned to the CX
 * user, grouped into:
 *   • Assigned Today  — assigned_today === true (server UTC date)
 *   • All Listings    — every assignment
 *
 * Each row drives the new Complete / Under Review / Reject workflow via
 * <OTATimerControl>, which captures Hotel ID / Listing URL through popups
 * and pauses SLA while a listing is Under Review.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, ExternalLink, CalendarClock, ListChecks, CheckCircle2 } from 'lucide-react';
import { roleInsightsAPI } from '../../services/api';
import OTATimerControl from '../../components/OTATimerControl';
import CompletedTimeCell from '../../components/CompletedTimeCell';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';
import { useTaskTable, TaskSearchBar, TablePagination } from '../../components/tasks/TableControls';

const SEARCH_KEYS = ['hotel_name', 'ota_name'];

const STATUS_BADGE = {
  not_assigned: 'bg-gray-100 text-gray-600',
  assigned:     'bg-blue-100 text-blue-700',
  in_progress:  'bg-amber-100 text-amber-700',
  under_review: 'bg-amber-100 text-amber-700',
  rejected:     'bg-red-100 text-red-700',
  completed:    'bg-emerald-100 text-emerald-700',
};

const OVERDUE_PALETTE = {
  Late:     'bg-amber-100 text-amber-700 border-amber-200',
  Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
  Critical: 'bg-rose-100 text-rose-700 border-rose-200',
};

// Build an `ota`-like object that <OTATimerControl> understands from a flat
// CX insights row. `assigned` (not yet started) maps to `pending`.
const rowToOta = (r) => ({
  listing_status: r.status === 'assigned' ? 'pending' : r.status,
  hotel_id: r.hotel_id,
  listing_url: r.listing_url,
  timer_start: r.timer_start,
  timer_running: r.timer_running,
  work_started_at: r.timer_start,
  completion_status: r.status === 'completed' ? 'Completed' : '',
  locked: r.url_locked,
  reject_count: r.reject_count,
  final_rejected: r.final_rejected,
});

const ListingsTable = ({ rows, onReload, tableTestId }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={tableTestId}>
      <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
        <tr>
          <th className="px-4 py-2.5 text-left">OTA</th>
          <th className="px-4 py-2.5 text-left">Hotel</th>
          <th className="px-4 py-2.5 text-left">Status</th>
          <th className="px-4 py-2.5 text-left">Assigned</th>
          <th className="px-4 py-2.5 text-left">Deadline</th>
          <th className="px-4 py-2.5 text-left">SLA</th>
          <th className="px-4 py-2.5 text-left">Overdue Time</th>
          <th className="px-4 py-2.5 text-left">Review Time</th>
          <th className="px-4 py-2.5 text-left">Completed Time</th>
          <th className="px-4 py-2.5 text-left">Hotel ID</th>
          <th className="px-4 py-2.5 text-left">URL</th>
          <th className="px-4 py-2.5 text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && (
          <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400 text-xs">No listings here.</td></tr>
        )}
        {rows.map((r) => {
          const statusCls = STATUS_BADGE[r.status] || STATUS_BADGE.not_assigned;
          const overdueLabel = (r.overdue_label || '').trim();
          const overdueCls = OVERDUE_PALETTE[overdueLabel] || null;
          return (
            <tr key={`${r.lead_id}-${r.ota_index}`} className="hover:bg-gray-50" data-testid={`cx-task-row-${r.lead_id}-${r.ota_index}`}>
              <td className="px-4 py-2.5 font-medium text-gray-800">{r.ota_name}</td>
              <td className="px-4 py-2.5 text-gray-700">{r.hotel_name}</td>
              <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusCls}`}>{(r.status || '').replace('_', ' ')}</span></td>
              <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{r.assigned_at ? new Date(r.assigned_at).toLocaleString() : '—'}</td>
              <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{r.deadline ? new Date(r.deadline).toLocaleString() : '—'}</td>
              <td className="px-4 py-2.5" data-testid={`cx-task-sla-${r.lead_id}-${r.ota_index}`}>
                {overdueLabel ? (
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${overdueCls}`}>
                    {overdueLabel}
                    {r.overdue_time && <span className="ml-1 text-[10px] font-normal opacity-80">· {r.overdue_time}</span>}
                  </span>
                ) : <span className="text-gray-300 text-xs">—</span>}
              </td>
              <td className="px-4 py-2.5 text-xs whitespace-nowrap" data-testid={`cx-task-overdue-${r.lead_id}-${r.ota_index}`}>
                {r.overdue_time ? <span className="text-rose-600 font-medium">{r.overdue_time}</span> : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{r.review_time || '—'}</td>
              <td className="px-4 py-2.5"><CompletedTimeCell ct={r.completed_time} /></td>
              <td className="px-4 py-2.5 text-xs text-gray-600">{r.hotel_id || '—'}</td>
              <td className="px-4 py-2.5 text-xs">
                {r.listing_url ? (
                  <a href={r.listing_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1">
                    <ExternalLink size={11} /> open
                  </a>
                ) : '—'}
              </td>
              <td className="px-4 py-2.5 text-right whitespace-nowrap">
                {r.status === 'assigned' && !r.approval_verified ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md" title="Account is still awaiting Executive final approval"
                    data-testid={`cx-task-blocked-${r.lead_id}-${r.ota_index}`}>
                    Awaiting approval
                  </span>
                ) : (
                  <div className="flex justify-end">
                    <OTATimerControl ota={rowToOta(r)} leadId={r.lead_id} otaIndex={r.ota_index} onReload={onReload} />
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const CXTaskListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Preserve the date filter passed from CX Insights (?range=&start=&end=).
  const [range, setRange] = useState(searchParams.get('range') || 'all');
  const [customFrom, setCustomFrom] = useState(searchParams.get('start') || '');
  const [customTo, setCustomTo] = useState(searchParams.get('end') || '');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await roleInsightsAPI.cx(rangeParams(range, customFrom, customTo));
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.rows || [], [data]);
  // Item 9 — completed listings must NOT appear in the active queues.
  const activeRows = useMemo(() => rows.filter((r) => r.status !== 'completed'), [rows]);
  const completedRows = useMemo(() => rows.filter((r) => r.status === 'completed'), [rows]);
  const assignedToday = useMemo(() => activeRows.filter((r) => r.assigned_today), [activeRows]);

  // Item 4 — CX Insights cards deep-link here with ?view=assigned|completed.
  const viewParam = (searchParams.get('view') || '').toLowerCase();
  const [view, setView] = useState(viewParam === 'completed' ? 'completed' : 'active');
  // Today / All toggle inside Active — a listing appears in exactly ONE of
  // these views, so it is never double-counted between Today and All.
  const [activeScope, setActiveScope] = useState('all');
  const shownActive = activeScope === 'today' ? assignedToday : activeRows;
  // Independent property search + pagination over the currently shown table.
  const shown = view === 'completed' ? completedRows : shownActive;
  const t = useTaskTable(shown, { keys: SEARCH_KEYS, pageSize: 10, resetKey: `${view}:${activeScope}` });
  useEffect(() => {
    if (viewParam === 'completed') setView('completed');
    else if (viewParam === 'assigned' || viewParam === 'active') setView('active');
  }, [viewParam]);

  const switchView = (v) => {
    setView(v);
    const next = new URLSearchParams(searchParams);
    next.set('view', v === 'completed' ? 'completed' : 'assigned');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-5" data-testid="cx-task-listings">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">My Listings</h2>
          <p className="text-sm text-gray-500 mt-0.5">Complete, move to Under Review, or Reject the OTA listings assigned to you.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="cx-task-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="cx-task-error">{error}</div>}

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="cx-task-date-filter" />

      {/* Active / Completed segmented control */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="cx-task-view-toggle">
        <button onClick={() => switchView('active')} data-testid="cx-task-view-active"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'active' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Active Listings
        </button>
        <button onClick={() => switchView('completed')} data-testid="cx-task-view-completed"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'completed' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
          Completed Listings
        </button>
      </div>

      <TaskSearchBar query={t.query} setQuery={t.setQuery} testId="cx-task-search" />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : view === 'completed' ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="cx-task-completed-wrap">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            <h3 className="text-sm font-bold text-gray-800">Completed Listings</h3>
            <span className="ml-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium" data-testid="cx-task-completed-count">{completedRows.length}</span>
          </div>
          <ListingsTable rows={t.pageRows} onReload={load} tableTestId="cx-task-completed-table" />
          <TablePagination page={t.page} totalPages={t.totalPages} setPage={t.setPage} total={t.total} pageSize={t.pageSize} testId="cx-task-completed-pagination" />
        </div>
      ) : (
        <>
          {/* Today / All toggle — a listing appears once, never double-counted */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="cx-task-scope-toggle">
            <button onClick={() => setActiveScope('today')} data-testid="cx-task-scope-today"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${activeScope === 'today' ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              <CalendarClock size={14} /> Assigned Today <span className="ml-1 opacity-80">({assignedToday.length})</span>
            </button>
            <button onClick={() => setActiveScope('all')} data-testid="cx-task-scope-all"
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${activeScope === 'all' ? 'bg-slate-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              <ListChecks size={14} /> All Listings <span className="ml-1 opacity-80">({activeRows.length})</span>
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="cx-task-active-wrap">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
              {activeScope === 'today' ? <CalendarClock size={16} className="text-teal-600" /> : <ListChecks size={16} className="text-slate-600" />}
              <h3 className="text-sm font-bold text-gray-800">{activeScope === 'today' ? 'Assigned Today' : 'All Listings'}</h3>
              <span className="ml-1 px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-[11px] font-medium" data-testid="cx-task-active-count">{shownActive.length}</span>
            </div>
            <ListingsTable rows={t.pageRows} onReload={load} tableTestId="cx-task-active-table" />
            <TablePagination page={t.page} totalPages={t.totalPages} setPage={t.setPage} total={t.total} pageSize={t.pageSize} testId="cx-task-active-pagination" />
          </div>
        </>
      )}
    </div>
  );
};

export default CXTaskListings;
